/**
 * Shared notification sounds (new order / customer requested / check bill).
 *
 * Browsers block audible playback until the user has interacted with the page
 * at least once (autoplay policy). Instead of unlocking per page, we unlock the
 * shared <audio> elements on the FIRST gesture anywhere in the app (bound from
 * the POS / KDS layout). By the time the user reaches sessions / KDS, the
 * sounds are already unlocked — no extra tap needed.
 *
 * Multiple sounds: every distinct source path gets its own cached <audio>
 * element. All registered elements are primed together on the first gesture.
 *
 * Usage:
 *   - Layout mounted():  initNotifySound()
 *   - Page on event:     playNotifySound(NOTIFY_SOUNDS.requested)
 *                        // or pass any require()'d audio path
 */

/** Built-in sound sources. Swap these files to change the chimes. */
export const NOTIFY_SOUNDS = {
  newOrder:  require('@/assets/sounds/new-order.wav'),          // KDS: new order
  requested: require('@/assets/sounds/customer-requested.wav'), // Session: customer requested
  checkBill: require('@/assets/sounds/check-bill.wav'),         // Session: check bill
};

// src (string) -> HTMLAudioElement
const sounds = new Map();
let unlocked = false;
let initialized = false;

function getAudio(src) {
  let a = sounds.get(src);
  if (!a) {
    a = new Audio(src);
    a.preload = 'auto';
    a.volume = 1.0;
    sounds.set(src, a);
    // If the app is already unlocked, best-effort prime the new element too.
    if (unlocked) prime(a).catch(() => {});
  }
  return a;
}

// Play once (muted) then immediately stop — registers media engagement so the
// element can be played later from a non-gesture context (e.g. a websocket
// callback). Muted so the user doesn't hear a blip during unlock; we restore
// the audible state once primed.
function prime(a) {
  a.muted = true;
  return a.play().then(() => {
    a.pause();
    a.currentTime = 0;
    a.muted = false;
  }).catch((err) => {
    a.muted = false;
    throw err;
  });
}

function removeListeners() {
  window.removeEventListener('pointerdown', unlock, true);
  window.removeEventListener('keydown', unlock, true);
  window.removeEventListener('touchstart', unlock, true);
}

// Runs on the first user gesture. Primes every registered sound. If any fails
// (e.g. still loading), we keep the listeners and retry on the next gesture.
function unlock() {
  if (unlocked) return;
  const all = [...sounds.values()].map((a) => prime(a));
  Promise.all(all).then(() => {
    unlocked = true;
    removeListeners();
    console.log('notify audio unlocked ✓ (' + sounds.size + ' sounds)');
  }).catch((err) => {
    console.warn('audio unlock failed, will retry on next gesture:', err && err.name);
  });
}

/**
 * Bind the global unlock listeners and register the built-in sounds so they
 * all unlock together on the first gesture. Idempotent — safe to call from
 * every layout that mounts. Pass extra sources to pre-register custom sounds.
 */
export function initNotifySound(extraSources = []) {
  // Pre-register built-in + any extra sounds so they unlock on the first gesture.
  [...Object.values(NOTIFY_SOUNDS), ...extraSources].forEach((src) => getAudio(src));

  if (initialized) return;
  initialized = true;
  // capture phase so a child's stopPropagation() can't swallow the gesture.
  window.addEventListener('pointerdown', unlock, true);
  window.addEventListener('keydown', unlock, true);
  window.addEventListener('touchstart', unlock, true);
}

/**
 * Play a notification sound by source path. No-op-safe if still locked.
 * @param {string} src - a value from NOTIFY_SOUNDS, or any require()'d audio path.
 */
export function playNotifySound(src) {
  if (!src) return;
  const a = getAudio(src);
  try {
    a.currentTime = 0;
    a.play().catch((err) => {
      console.warn('notify sound blocked:', err && err.name);
    });
  } catch (e) {
    // best-effort; ignore audio failures
  }
}

/** Whether the shared sounds have been unlocked by a user gesture yet. */
export function isNotifySoundUnlocked() {
  return unlocked;
}
